import { AlertTriangle, Check, Lock, Shield, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Controller } from "react-hook-form";
import { z } from "zod";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlertDialog } from "@/shared/components/feedback/ColocaAlertDialog";
import { ColocaInput } from "@/shared/components/forms/ColocaInput";
import { ColocaTextarea } from "@/shared/components/forms/ColocaTextarea";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { colocaToast, toastApiError } from "@/shared/utils/toast";

import type { RoleMembersState } from "../../hooks/useRoleMembers";
import { rolesService } from "../../services/rolesService";
import type { AccessPolicy, AccessRole } from "../../types/access.types";
import { gatherStatements } from "../../utils/denyFirst";
import { AccessScopeBadge } from "./AccessScopeBadge";
import { EffectiveMatrix } from "./EffectiveMatrix";
import { PrincipalAvatar } from "./PrincipalAvatar";

export interface RoleEditorDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The role being edited/viewed, or `null` when creating a new one. */
  role: AccessRole | null;
  /** When true the editor is view-only (protected role, or global for an org user, or no permission). */
  readOnly: boolean;
  /** Full policies catalog to link from. */
  policies: readonly AccessPolicy[];
  /**
   * A preview of the USERS holding this role, fetched server-side by `role_id`.
   *
   * Deliberately not "the members": `role.member_count` counts users **and** API keys, and there is no
   * endpoint that lists API keys by role, so this list is always a labelled subset of that total.
   */
  memberPreview?: RoleMembersState;
  /** Whether the current user may delete this role (combined with role writability). */
  canDelete: boolean;
  /** Called after a successful create/update — parent reloads and closes. */
  onSaved: () => void;
  /** Called after a successful delete — parent reloads and closes. */
  onDeleted: () => void;
}

/**
 * Create / edit / view a role. A role bundles policies; its effective access is the deny-first resolution of
 * those policies' statements, previewed live as the selection changes. `policy_ids` is sent whole on save
 * (the backend replaces the linked set). Protected roles — and global roles for org users — open read-only.
 *
 * Text fields are RHF + Zod (module pattern, see `CreateApiKeyForm`); the limits mirror the backend
 * `CreateRoleDto` (name ≤ 120, description ≤ 500). The policy picker is intentionally NOT form state — it is
 * a toggle grid over the catalog, kept as a local `Set`.
 */
export function RoleEditorDrawer({
  open,
  onOpenChange,
  role,
  readOnly,
  policies,
  memberPreview,
  canDelete,
  onSaved,
  onDeleted,
}: RoleEditorDrawerProps) {
  const { t } = useTranslations("settings");
  const { isInternalUser } = useAuth();

  const schema = useMemo(
    () =>
      z.object({
        name: z
          .string()
          .trim()
          .min(1, t("access_role_name_required"))
          .max(120, t("access_validation_name_max")),
        description: z.string().trim().max(500, t("access_validation_desc_max")),
      }),
    [t],
  );

  const form = useZodForm(schema, { name: "", description: "" });
  const nameValue = form.watch("name");

  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Seed the form when the drawer opens (for a new role or a different one). `role` is parent state that only
  // changes together with `open`, so this runs exactly once per open.
  useEffect(() => {
    if (!open) return;
    form.reset({ name: role?.name ?? "", description: role?.description ?? "" });
    setSelectedIds(new Set(role?.policies.map((p) => p.id) ?? []));
  }, [open, role, form]);

  const allowPolicies = useMemo(() => policies.filter((p) => p.effect === "allow"), [policies]);
  const denyPolicies = useMemo(() => policies.filter((p) => p.effect === "deny"), [policies]);

  const statements = useMemo(() => {
    const selected = policies.filter((p) => selectedIds.has(p.id));
    // Name/description are irrelevant to the deny-first resolution — only the linked policies matter.
    const draft: AccessRole = {
      id: role?.id ?? "__draft",
      slug: role?.slug ?? "__draft",
      name: role?.name ?? "",
      description: role?.description,
      organization_id: role?.organization_id ?? null,
      is_default: role?.is_default ?? false,
      is_protected: role?.is_protected ?? false,
      policies: selected,
      // Irrelevant here: this draft exists only to resolve the deny-first preview of the selected
      // policies. The real count comes from the catalog and is rendered from `role`, not from this.
      member_count: 0,
      created_at: role?.created_at ?? "",
      updated_at: role?.updated_at ?? "",
    };
    return gatherStatements([draft.id], [draft]);
  }, [policies, selectedIds, role]);

  const togglePolicy = (id: string) => {
    if (readOnly) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onValid = async (values: z.output<typeof schema>) => {
    setIsSaving(true);
    try {
      const body = {
        name: values.name,
        description: values.description || undefined,
        policy_ids: [...selectedIds],
      };
      if (role) await rolesService.update(role.id, body);
      else await rolesService.create(body);
      colocaToast.success(role ? t("access_role_updated") : t("access_role_created"));
      onSaved();
    } catch (e) {
      toastApiError(e);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!role) return;
    try {
      await rolesService.remove(role.id);
      colocaToast.success(t("access_role_deleted"));
      onDeleted();
    } catch (e) {
      toastApiError(e);
    }
  };

  const title = role
    ? readOnly
      ? t("access_role_editor_view")
      : t("access_role_editor_edit")
    : t("access_role_editor_new");

  return (
    <ColocaDrawer
      open={open}
      onOpenChange={onOpenChange}
      size="xl"
      title={
        <span className="flex items-center gap-2.5">
          {readOnly && <Lock size={14} className="text-muted-foreground" />}
          {title}
          {role && <AccessScopeBadge organizationId={role.organization_id} />}
        </span>
      }
      description={role ? role.name : t("access_role_editor_new_sub")}
      footer={
        <div className="flex w-full items-center gap-2">
          {role && canDelete && !readOnly && (
            <ColocaButton
              variant="ghost"
              size="sm"
              icon={Trash2}
              className="text-destructive"
              onClick={() => setDeleteOpen(true)}
            >
              {t("access_role_delete")}
            </ColocaButton>
          )}
          <div className="ml-auto flex items-center gap-2">
            <ColocaButton
              variant={readOnly ? "default" : "ghost"}
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              {readOnly ? t("access_drawer_close") : t("access_role_cancel")}
            </ColocaButton>
            {!readOnly && (
              <ColocaButton
                variant="primary"
                size="sm"
                icon={Check}
                loading={isSaving}
                disabled={!nameValue.trim()}
                onClick={form.handleSubmit(onValid)}
              >
                {role ? t("access_role_save") : t("access_role_create_action")}
              </ColocaButton>
            )}
          </div>
        </div>
      }
    >
      {readOnly && (
        <div className="mb-4 flex items-center gap-2.5 rounded-md border border-warning/40 bg-warning/10 px-3 py-2.5 text-[12px] text-foreground">
          <Lock size={12} className="text-warning" />
          {t("access_role_readonly_note")}
        </div>
      )}

      {/* Identity — RHF + Zod (see CreateApiKeyForm for the module pattern) */}
      <Controller
        control={form.control}
        name="name"
        render={({ field, fieldState }) => (
          <div className="mb-4">
            <label
              htmlFor="role-editor-name"
              className="mb-1 block text-[12.5px] font-medium text-foreground"
            >
              {t("access_role_name_label")}
            </label>
            <ColocaInput
              id="role-editor-name"
              ref={field.ref}
              value={field.value}
              onValueChange={field.onChange}
              onBlur={field.onBlur}
              disabled={readOnly}
              placeholder={t("access_role_name_placeholder")}
              aria-invalid={!!fieldState.error}
            />
            {fieldState.error && (
              <p className="mt-1 flex items-center gap-1.5 text-[11.5px] text-destructive">
                <AlertTriangle size={12} /> {fieldState.error.message}
              </p>
            )}
          </div>
        )}
      />

      <Controller
        control={form.control}
        name="description"
        render={({ field, fieldState }) => (
          <div className="mb-5">
            <label
              htmlFor="role-editor-desc"
              className="mb-1 block text-[12.5px] font-medium text-foreground"
            >
              {t("access_role_desc_label")}
            </label>
            <ColocaTextarea
              id="role-editor-desc"
              ref={field.ref}
              value={field.value}
              onValueChange={field.onChange}
              onBlur={field.onBlur}
              disabled={readOnly}
              rows={2}
              placeholder={t("access_role_desc_placeholder")}
              aria-invalid={!!fieldState.error}
            />
            {fieldState.error && (
              <p className="mt-1 flex items-center gap-1.5 text-[11.5px] text-destructive">
                <AlertTriangle size={12} /> {fieldState.error.message}
              </p>
            )}
          </div>
        )}
      />

      {/* Policy picker */}
      <div className="mb-2.5 flex items-center gap-2 border-b border-dashed border-border pb-2">
        <span className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.06em] text-foreground">
          {t("access_role_policies_label")}
        </span>
        <span className="font-mono text-[10.5px] text-muted-foreground">· {selectedIds.size}</span>
      </div>

      <PolicyPickGroup
        label={t("access_role_policies_allow")}
        policies={allowPolicies}
        selectedIds={selectedIds}
        readOnly={readOnly}
        onToggle={togglePolicy}
      />
      <div className="h-3.5" />
      <PolicyPickGroup
        label={t("access_role_policies_deny")}
        danger
        hint={t("access_role_policies_deny_hint")}
        policies={denyPolicies}
        selectedIds={selectedIds}
        readOnly={readOnly}
        onToggle={togglePolicy}
      />

      {/* Live effective matrix */}
      <div className="mt-5 overflow-hidden rounded-md border border-border">
        <div className="flex items-center gap-2 border-b border-border bg-muted/40 px-3.5 py-2.5">
          <Shield size={13} className="text-primary" />
          <span className="text-[12.5px] font-semibold text-foreground">
            {t("access_role_effective_title")}
          </span>
          <span className="ml-auto text-[11px] text-muted-foreground">
            {t("access_drawer_effective_hint")}
          </span>
        </div>
        <div className="p-2.5">
          {selectedIds.size === 0 ? (
            <div className="px-2 py-4 text-center text-[12.5px] text-muted-foreground">
              {t("access_role_no_policies_empty")}
            </div>
          ) : (
            <EffectiveMatrix statements={statements} />
          )}
        </div>
      </div>

      {/* Who holds this role. The headline is the backend's `member_count` (users + API keys, scoped to the
          caller's tenant); the list below is only the USER half, labelled as such, because no endpoint can
          list API keys by role. Presenting the list as "the members" would under-report the blast radius. */}
      {role && (
        <div className="mt-5">
          {/* Qualified by tenancy for the same reason as the role card: the backend scopes `member_count`
              to the caller's organization, so an unlabelled figure is false for one of the two audiences.
              It matters more here — this is the panel that holds the delete button. */}
          <div className="mb-2 font-mono text-[10.5px] uppercase tracking-[0.06em] text-muted-foreground">
            {t("access_role_members_label")}
            <span className="ml-1.5 text-muted-foreground/70">
              ·{" "}
              {isInternalUser
                ? role.member_count === 1
                  ? t("access_role_member_count_platform_one")
                  : t("access_role_member_count_platform", { count: role.member_count })
                : role.member_count === 1
                  ? t("access_role_member_count_org_one")
                  : t("access_role_member_count_org", { count: role.member_count })}
            </span>
          </div>
          {memberPreview?.hasFailed ? (
            <div className="text-[12px] text-muted-foreground/70">{t("access_role_members_unavailable")}</div>
          ) : role.member_count === 0 ? (
            <div className="text-[12px] text-muted-foreground/70">{t("access_role_no_members")}</div>
          ) : (
            <div className="flex flex-col gap-1.5">
              <div className="text-[11px] text-muted-foreground/70">
                {t("access_role_members_users_label")}
              </div>
              {memberPreview?.members.map((m) => (
                <div key={m.id} className="flex items-center gap-2">
                  <PrincipalAvatar name={m.name} kind={m.kind} size={22} />
                  <span className="text-[12px] text-foreground">{m.name}</span>
                </div>
              ))}
              {!memberPreview?.isLoading &&
                (memberPreview?.usersTotal ?? 0) > (memberPreview?.members.length ?? 0) && (
                  <span className="font-mono text-[10.5px] text-muted-foreground/70">
                    {t("access_role_members_more", {
                      count: (memberPreview?.usersTotal ?? 0) - (memberPreview?.members.length ?? 0),
                    })}
                  </span>
                )}
              {/* The gap between the tenant-wide count and the users we can list IS the API keys. Saying so
                  is more honest than letting the two numbers silently disagree — but ONLY once the users
                  have actually loaded: while `usersTotal` is still 0 the gap is the whole count, and this
                  would claim API keys hold the role on every single open. */}
              {!memberPreview?.isLoading && role.member_count > (memberPreview?.usersTotal ?? 0) && (
                <span className="text-[11px] text-muted-foreground/70">
                  {t("access_role_members_keys_note")}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {role && (
        <ColocaAlertDialog
          trigger={<span style={{ display: "none" }} />}
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          media={<Trash2 size={24} className="text-destructive" />}
          title={t("access_role_delete_title")}
          /* `member_count`, not the loaded list. This used to count the principals that happened to be on
             the loaded page, so deleting a role held by 400 users could show the "nobody uses it" copy —
             and the delete cascades to every assignment server-side. The number here is the blast radius. */
          description={
            role.member_count > 0
              ? t("access_role_delete_desc_members", { name: role.name, count: role.member_count })
              : t("access_role_delete_desc", { name: role.name })
          }
          confirmLabel={t("access_role_delete_confirm")}
          cancelLabel={t("access_role_cancel")}
          confirmVariant="destructive"
          onConfirm={handleDelete}
          onCancel={() => setDeleteOpen(false)}
        />
      )}
    </ColocaDrawer>
  );
}

interface PolicyPickGroupProps {
  label: string;
  danger?: boolean;
  hint?: string;
  policies: readonly AccessPolicy[];
  selectedIds: Set<string>;
  readOnly: boolean;
  onToggle: (id: string) => void;
}

function PolicyPickGroup({
  label,
  danger,
  hint,
  policies,
  selectedIds,
  readOnly,
  onToggle,
}: PolicyPickGroupProps) {
  const { t } = useTranslations("settings");

  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <span
          className={cn("h-1.5 w-1.5 rounded-sm", danger ? "bg-destructive" : "bg-primary")}
          aria-hidden
        />
        <span className="font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
          {label}
        </span>
        {hint && <span className="text-[11px] text-muted-foreground/70">{hint}</span>}
      </div>
      {policies.length === 0 ? (
        <div className="px-0.5 py-1 text-[11.5px] text-muted-foreground/70">
          {t("access_role_policies_none_available")}
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          {policies.map((policy) => {
            const on = selectedIds.has(policy.id);
            const accent = danger ? "border-destructive bg-destructive/10" : "border-primary bg-primary/10";
            return (
              <button
                key={policy.id}
                type="button"
                role="checkbox"
                aria-checked={on}
                disabled={readOnly}
                onClick={() => onToggle(policy.id)}
                className={cn(
                  "flex items-start gap-2.5 rounded-md border px-2.5 py-2 text-left transition-colors",
                  on ? accent : "border-border bg-card hover:border-border/80",
                  readOnly && "cursor-default",
                )}
              >
                <span
                  className={cn(
                    "mt-0.5 grid h-[18px] w-[18px] shrink-0 place-items-center rounded border",
                    on
                      ? danger
                        ? "border-destructive bg-destructive text-white"
                        : "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-card",
                  )}
                >
                  {on && <Check size={11} />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5">
                    <span className="text-[12.5px] font-semibold text-foreground">{policy.name}</span>
                    {!policy.organization_id && <Lock size={9} className="text-muted-foreground/70" />}
                    <span className="ml-auto font-mono text-[10px] text-muted-foreground/70">
                      {policy.statements.length}
                    </span>
                  </span>
                  {policy.description && (
                    <span className="mt-0.5 block text-[11px] text-muted-foreground">
                      {policy.description}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
